/**
 * CDN Store - Multi-tenant CDN URL Management
 *
 * Armazena o CDN URL do cliente ativo para uso em toda a aplicação.
 * O CDN URL é obtido do backend via /api/v1/core/dev/storage.
 *
 * IMPORTANTE: Em produção, NUNCA usar fallback hardcoded.
 * Cada cliente tem seu próprio CDN (CloudFront distribution).
 * Usar CDN errado = imagens quebradas ou dados de outro cliente.
 *
 * @since 2026-01-12
 * @updated 2026-02-19 - Removido fallback hardcoded para demo CDN
 */

import { create } from 'zustand';

interface StorageInfo {
  active_client: string | null;
  storage_mode: 'local' | 'production';
  bucket: string | null;
  cdn_url: string | null;
}

interface CdnState {
  /** CDN URL do cliente ativo */
  cdnUrl: string | null;

  /** Cliente ativo */
  activeClient: string | null;

  /** Storage mode (local/production) */
  storageMode: 'local' | 'production';

  /** Se já foi carregado */
  isLoaded: boolean;

  /** Se está carregando */
  isLoading: boolean;

  /** Atualiza o CDN URL */
  setCdnUrl: (url: string | null) => void;

  /** Carrega info do backend */
  loadFromBackend: () => Promise<void>;

  /** Reset */
  reset: () => void;
}

export const useCdnStore = create<CdnState>((set, get) => ({
  cdnUrl: null,
  activeClient: null,
  storageMode: 'local',
  isLoaded: false,
  isLoading: false,

  setCdnUrl: (url) => set({ cdnUrl: url }),

  loadFromBackend: async () => {
    if (get().isLoading) return;

    set({ isLoading: true });

    try {
      const response = await fetch('/api/v1/core/dev/storage');
      if (response.ok) {
        const data: StorageInfo = await response.json();
        if (data.cdn_url) {
          set({
            cdnUrl: data.cdn_url,
            activeClient: data.active_client,
            storageMode: data.storage_mode,
            isLoaded: true,
            isLoading: false,
          });
        } else {
          // Backend retornou mas sem CDN URL - erro de configuração
          console.error('❌ Backend retornou cdn_url=null. Verifique CLIENT_CODE e storage configs.');
          set({
            cdnUrl: null,
            activeClient: data.active_client,
            storageMode: data.storage_mode,
            isLoaded: true,
            isLoading: false,
          });
        }
      } else {
        console.error('❌ Falha ao carregar CDN config do backend:', response.status);
        set({
          cdnUrl: null,
          isLoaded: true,
          isLoading: false,
        });
      }
    } catch (error) {
      console.error('❌ Falha ao carregar CDN config:', error);
      set({
        cdnUrl: null,
        isLoaded: true,
        isLoading: false,
      });
    }
  },

  reset: () => set({
    cdnUrl: null,
    activeClient: null,
    storageMode: 'local',
    isLoaded: false,
    isLoading: false,
  }),
}));

/**
 * Obtém o CDN URL atual (síncrono)
 * Retorna o URL do store ou string vazia se não carregado
 */
export function getCurrentCdnUrl(): string {
  const state = useCdnStore.getState();
  return state.cdnUrl || '';
}

export default useCdnStore;
